import { DUPE_API_URL } from '@dupe/common/constants'
import { useQuery } from '@tanstack/react-query'
import useAuthUser from '~/src/hooks/useAuthUser'
import useGetTempUserId from '~/src/hooks/useGetTempUserId'
import fetcher from '~/src/lib/fetcher'

// Unified recent item that supports both dupes and chats

export enum UnifiedRecentItemType {
  Dupe = 'dupe',
  Chat = 'chat',
  ChatV2 = 'chat-v2',
  Research = 'research',
}

const DEFAULT_TYPES: UnifiedRecentItemType[] = [
  UnifiedRecentItemType.Dupe,
  UnifiedRecentItemType.Chat,
]

export interface UnifiedRecentItem {
  id: string
  type: UnifiedRecentItemType
  title: string
  image?: string
  url: string
  timestamp: Date
  // For dupes
  hash?: string
  source?: string
  // For chats
  threadId?: string
  summary?: string
  slug?: string
  images?: string[]
  /** A fragrance chat: show the fragrance icon when `image` is empty. */
  fragrance?: boolean
  /** furniture, fashion or fragrance, when the api could place it (the Library filters). */
  category?: string | null
  // For research tasks
  taskId?: string
  researchStatus?: string
}

export type RecentsResponse = {
  recents: UnifiedRecentItem[]
  hasMore: boolean
}

interface Props {
  limit?: number
  enabled?: boolean
  orderBy?: 'asc' | 'desc'
  types?: UnifiedRecentItemType[]
}

const useFetchRecents = (props?: Props) => {
  const {
    limit = 10,
    enabled = true,
    orderBy = 'desc',
    types = DEFAULT_TYPES,
  } = props || {}
  const { tempUserId } = useGetTempUserId()
  const { authUser, loading: loadingAuthUser } = useAuthUser()
  const fetchUrl = `${DUPE_API_URL}/api/recents`

  const id = authUser?.uid || tempUserId
  const loading = loadingAuthUser
  // Sort + dedupe so caller-ordering or accidental duplicates don't produce
  // different cache keys (or query strings) for the same logical set.
  // Empty arrays fall back to the default so we never send `types=`.
  const typesParam = Array.from(
    new Set((types.length > 0 ? types : DEFAULT_TYPES).slice().sort())
  ).join(',')

  return useQuery<RecentsResponse, Error>({
    queryKey: [
      'recents',
      id,
      limit,
      orderBy,
      authUser?.uid,
      tempUserId,
      typesParam,
    ],
    queryFn: () =>
      fetcher<RecentsResponse>(fetchUrl, {
        method: 'GET',
        dataOrParams: {
          limit,
          orderBy,
          types: typesParam,
        },
        tempUserId,
        authUser,
      }),
    enabled: !!id && enabled && !loading,
    refetchOnWindowFocus: false,
  })
}

export default useFetchRecents
